import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chess, type Square } from 'chess.js';
import { PieceSvg, type Piece } from './pieces';

/**
 * An interactive chessboard.
 *
 * Move input works two ways at once, because both are muscle memory for
 * different players: click a piece then click a destination, or drag it. Legal
 * destinations are shown as soon as a piece is picked up, since this is a
 * teaching tool — hiding them would test board vision rather than opening
 * knowledge, which is not what the app is for.
 *
 * The board is a controlled component: it renders the `fen` it is given and
 * reports attempted moves through `onMove`. It never mutates the position
 * itself, so the trainer stays the single source of truth about what is legal
 * *in the line* as opposed to legal on the board.
 */

export type BoardTheme = 'walnut' | 'slate' | 'green' | 'ice';

export interface AttemptedMove {
  from: Square;
  to: Square;
  /** Set only when the move is a promotion. */
  promotion?: 'q' | 'r' | 'b' | 'n';
}

interface ChessboardProps {
  fen: string;
  orientation: 'white' | 'black';
  /**
   * Called for any move that is legal on the board. Return value is ignored —
   * the parent decides whether to advance the position, so a legal-but-wrong
   * move can be rejected and shown as a mistake.
   */
  onMove?: (move: AttemptedMove) => void;
  /** Disables all input (opponent's turn, line finished, review mode). */
  disabled?: boolean;
  /** The move just played, highlighted from-and-to. */
  lastMove?: { from: string; to: string } | null;
  /** Squares to flash — used to show the correct move after a mistake. */
  hintMove?: { from: string; to: string } | null;
  theme?: BoardTheme;
  showCoordinates?: boolean;
  /** Maximum edge length in px; the board is always square. */
  maxSize?: number;
}

const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;
const RANKS = ['8', '7', '6', '5', '4', '3', '2', '1'] as const;

const THEMES: Record<BoardTheme, { light: string; dark: string }> = {
  walnut: { light: '#e9d7b8', dark: '#9b6b43' },
  slate: { light: '#cfd6de', dark: '#5c6b7d' },
  green: { light: '#eeeed2', dark: '#769656' },
  ice: { light: '#dfe8ef', dark: '#7d9bb3' },
};

/** Board squares in display order for the given orientation. */
function squaresFor(orientation: 'white' | 'black'): Square[] {
  const files = orientation === 'white' ? FILES : [...FILES].reverse();
  const ranks = orientation === 'white' ? RANKS : [...RANKS].reverse();
  return ranks.flatMap((rank) => files.map((file) => `${file}${rank}` as Square));
}

function isDark(square: string): boolean {
  const file = square.charCodeAt(0) - 97;
  const rank = Number(square[1]) - 1;
  return (file + rank) % 2 === 0;
}

export default function Chessboard({
  fen,
  orientation,
  onMove,
  disabled = false,
  lastMove = null,
  hintMove = null,
  theme = 'walnut',
  showCoordinates = true,
  maxSize = 560,
}: ChessboardProps) {
  const [selected, setSelected] = useState<Square | null>(null);
  const [dragFrom, setDragFrom] = useState<Square | null>(null);
  const [pendingPromotion, setPendingPromotion] = useState<AttemptedMove | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);

  // Parsing the FEN is the only chess logic the board itself does: it needs to
  // know what sits on each square and which moves are legal to preview.
  const game = useMemo(() => {
    const chess = new Chess();
    try {
      chess.load(fen);
    } catch {
      // A malformed FEN should render an empty board rather than crash the page.
      return null;
    }
    return chess;
  }, [fen]);

  const board = useMemo(() => {
    const map = new Map<string, Piece>();
    if (!game) return map;
    for (const row of game.board()) {
      for (const cell of row) {
        if (cell) map.set(cell.square, { type: cell.type, color: cell.color });
      }
    }
    return map;
  }, [game]);

  // Clear any in-progress selection whenever the position changes underneath
  // us — otherwise a stale selection can produce a move on the wrong board.
  useEffect(() => {
    setSelected(null);
    setDragFrom(null);
    setPendingPromotion(null);
  }, [fen]);

  const legalTargets = useMemo(() => {
    if (!game || !selected) return new Set<string>();
    return new Set(game.moves({ square: selected, verbose: true }).map((m) => m.to));
  }, [game, selected]);

  const isPromotion = useCallback(
    (from: Square, to: Square): boolean => {
      if (!game) return false;
      const piece = board.get(from);
      if (!piece || piece.type !== 'p') return false;
      const targetRank = to[1];
      return (piece.color === 'w' && targetRank === '8') || (piece.color === 'b' && targetRank === '1');
    },
    [game, board],
  );

  const attempt = useCallback(
    (from: Square, to: Square) => {
      if (!game || !onMove) return;
      // Only offer the move if it is legal on the board. Legality *in the line*
      // is the trainer's business, not the board's.
      const legal = game.moves({ square: from, verbose: true }).some((m) => m.to === to);
      if (!legal) return;

      if (isPromotion(from, to)) {
        setPendingPromotion({ from, to });
        return;
      }
      onMove({ from, to });
    },
    [game, onMove, isPromotion],
  );

  const handleSquareClick = useCallback(
    (square: Square) => {
      if (disabled || !game) return;
      const piece = board.get(square);

      if (selected) {
        if (square === selected) {
          setSelected(null);
          return;
        }
        // Clicking another of your own pieces re-selects rather than failing.
        if (piece && piece.color === game.turn()) {
          setSelected(square);
          return;
        }
        attempt(selected, square);
        setSelected(null);
        return;
      }

      if (piece && piece.color === game.turn()) setSelected(square);
    },
    [disabled, game, board, selected, attempt],
  );

  const squares = useMemo(() => squaresFor(orientation), [orientation]);

  if (!game) {
    return <div className="board-error">Unable to display this position.</div>;
  }

  const colors = THEMES[theme] ?? THEMES.walnut;
  const turn = game.turn();
  // The king in check gets its own marker — without it, beginners miss checks.
  const checkSquare = game.inCheck()
    ? [...board.entries()].find(([, p]) => p.type === 'k' && p.color === turn)?.[0]
    : undefined;

  return (
    <div className="board-wrap" style={{ maxWidth: maxSize }}>
      <div
        ref={boardRef}
        className="board"
        style={{ ['--sq-light' as string]: colors.light, ['--sq-dark' as string]: colors.dark }}
        role="grid"
        aria-label="Chess board"
      >
        {squares.map((square) => {
          const piece = board.get(square);
          const isSelected = selected === square;
          const isTarget = legalTargets.has(square);
          const isLast = lastMove && (lastMove.from === square || lastMove.to === square);
          const isHint = hintMove && (hintMove.from === square || hintMove.to === square);
          const canDrag = !disabled && !!piece && piece.color === turn;

          return (
            <div
              key={square}
              role="gridcell"
              aria-label={square}
              className={[
                'sq',
                isDark(square) ? 'sq--dark' : 'sq--light',
                isSelected ? 'sq--selected' : '',
                isLast ? 'sq--last' : '',
                isHint ? 'sq--hint' : '',
                checkSquare === square ? 'sq--check' : '',
                canDrag ? 'sq--grab' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => handleSquareClick(square)}
              onDragOver={(e) => {
                if (!disabled && dragFrom) e.preventDefault();
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (disabled || !dragFrom) return;
                attempt(dragFrom, square);
                setDragFrom(null);
                setSelected(null);
              }}
            >
              {piece && (
                <div
                  className="sq-piece"
                  draggable={canDrag}
                  onDragStart={() => {
                    setDragFrom(square);
                    setSelected(square);
                  }}
                  onDragEnd={() => setDragFrom(null)}
                >
                  <PieceSvg piece={piece} />
                </div>
              )}
              {/* A dot for an empty target, a ring for a capture. */}
              {isTarget && <span className={piece ? 'sq-capture' : 'sq-dot'} />}
              {showCoordinates && square[1] === (orientation === 'white' ? '1' : '8') && (
                <span className="sq-coord sq-coord--file">{square[0]}</span>
              )}
              {showCoordinates && square[0] === (orientation === 'white' ? 'a' : 'h') && (
                <span className="sq-coord sq-coord--rank">{square[1]}</span>
              )}
            </div>
          );
        })}
      </div>

      {pendingPromotion && (
        <div className="promo-overlay" role="dialog" aria-label="Choose a promotion piece">
          <div className="promo-card">
            <span className="promo-label">Promote to</span>
            <div className="promo-options">
              {(['q', 'r', 'b', 'n'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  className="promo-btn"
                  onClick={() => {
                    onMove?.({ ...pendingPromotion, promotion: type });
                    setPendingPromotion(null);
                  }}
                >
                  <PieceSvg piece={{ type, color: turn }} size={40} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
